/**
 * Mavzu (light / dark) boshqaruvi.
 *
 * Uchta manba, shu tartibda:
 *  1. foydalanuvchi qo'lda tanlagani (localStorage);
 *  2. Telegram mijozining mavzusi (`WebApp.colorScheme`) — ilova Telegram
 *     ichida ochilgani uchun standart holatda unga ergashish to'g'ri;
 *  3. tizim sozlamasi (`prefers-color-scheme`).
 *
 * Ilgari `index.html` da `class="dark"` qattiq yozilgan edi va light mode
 * umuman yo'q edi.
 */

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = ThemeMode | 'auto';

const STORAGE_KEY = 'shaftoli-theme';

function telegramScheme(): ThemeMode | null {
  const scheme = (window as any).Telegram?.WebApp?.colorScheme;
  return scheme === 'light' || scheme === 'dark' ? scheme : null;
}

function systemScheme(): ThemeMode {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function readPreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'auto') return saved;
  } catch {
    // localStorage ba'zi WebView'larda o'chirilgan bo'lishi mumkin
  }
  return 'auto';
}

export function resolveTheme(preference: ThemePreference): ThemeMode {
  if (preference !== 'auto') return preference;
  return telegramScheme() ?? systemScheme();
}

/** Mavzuni DOM ga qo'llaydi va Telegram chrome ranglarini moslaydi. */
export function applyTheme(mode: ThemeMode): void {
  document.documentElement.setAttribute('data-theme', mode);

  // Telegram sarlavha/fon ranglarini bir xil qilamiz, aks holda ilova
  // atrofida boshqa rangdagi ramka ko'rinib qoladi.
  const surface = mode === 'dark' ? '#16130f' : '#faf7f2';
  try {
    const twa = (window as any).Telegram?.WebApp;
    twa?.setHeaderColor?.(surface);
    twa?.setBackgroundColor?.(surface);
  } catch {
    // Telegramdan tashqarida ishga tushirilgan
  }

  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', surface);
}

export function savePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // jimgina o'tkazamiz
  }
}

/**
 * Mavzuni React yuklanishidan OLDIN qo'llash uchun. `main.tsx` da
 * chaqiriladi — aks holda birinchi kadr noto'g'ri rangda chiqib ketadi.
 */
export function initTheme(): ThemeMode {
  const mode = resolveTheme(readPreference());
  applyTheme(mode);
  return mode;
}

/** `auto` rejimda tizim mavzusi o'zgarsa kuzatib borish. */
export function watchSystemTheme(
  onChange: (mode: ThemeMode) => void,
): () => void {
  const media = window.matchMedia?.('(prefers-color-scheme: dark)');
  if (!media) return () => {};

  const handler = () => {
    if (readPreference() === 'auto') onChange(resolveTheme('auto'));
  };

  media.addEventListener('change', handler);
  return () => media.removeEventListener('change', handler);
}
